import React from 'react';
import { authFetch } from '../lib/authFetch';
import { t } from '../lib/i18n';
import Icon from '../components/Icon';

const AuthCard = ({ title, children }) => (
    <div className="flex min-h-screen items-center justify-center bg-[#FAFAFA] p-4">
        <div className="w-full max-w-md">
            <div className="mb-8 text-center">
                <div className="w-12 h-12 rounded-xl bg-[#AA5D26] flex items-center justify-center mx-auto mb-3">
                    <Icon name="shield-check" className="w-6 h-6 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-[#AA5D26] font-heading">MTA Group</h1>
                <p className="mt-1 text-sm text-[#9CA3AF]">Audytor PRO</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm">
                <div className="px-6 pt-6 pb-2">
                    <h2 className="text-xl font-semibold text-center text-[#1A1A2E] font-heading">{title}</h2>
                </div>
                <div className="px-6 pb-6">{children}</div>
            </div>
        </div>
    </div>
);

/**
 * LoginScreen — full auth login screen with:
 *   - Email/password form with "remember me"
 *   - Forgot password flow (request reset → email sent)
 *   - Reset password flow (via ?reset=TOKEN in URL)
 *   - 2FA verification step
 *
 * Props:
 *   onLogin(user, mustChange) — called after successful login
 *   sessionExpiredMsg — optional boolean, shows session-expired banner
 */
export default function LoginScreen({ onLogin, sessionExpiredMsg }) {
    const [email, setEmail] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [rememberMe, setRememberMe] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState('');
    const [showForgot, setShowForgot] = React.useState(false);
    const [forgotEmail, setForgotEmail] = React.useState('');
    const [forgotSent, setForgotSent] = React.useState(false);
    const [resetToken] = React.useState(() => {
        try { return new URLSearchParams(window.location.search).get('reset') || null; } catch(e) { return null; }
    });
    const [newPassword, setNewPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');
    const [showNewPw, setShowNewPw] = React.useState(false);
    const [resetDone, setResetDone] = React.useState(false);
    // 2FA state
    const [show2FA, setShow2FA] = React.useState(false);
    const [tempToken, setTempToken] = React.useState(null);
    const [twofaCode, setTwofaCode] = React.useState('');

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true); setError('');
        try {
            const r = await authFetch('/api/auth/login', {
                method: 'POST',
                body: JSON.stringify({ email, password, rememberMe })
            });
            const data = await r.json();
            if (r.status === 202 && data.requires2FA) {
                setTempToken(data.tempToken);
                setShow2FA(true);
                setTwofaCode('');
                setLoading(false);
                return;
            }
            if (!r.ok) throw new Error(data.error || t('error_login'));
            onLogin(data.user, data.mustChangePassword);
        } catch(err) {
            if (err.name === 'AbortError') setError(t('auth_timeout'));
            else setError(err.message);
        }
        finally { setLoading(false); }
    };

    const handleVerify2FA = async (e) => {
        e.preventDefault();
        if (!twofaCode.trim() || !tempToken) return;
        setLoading(true); setError('');
        try {
            const r = await authFetch('/api/auth/verify-2fa', {
                method: 'POST',
                body: JSON.stringify({ tempToken, code: twofaCode.trim() })
            });
            const data = await r.json();
            if (!r.ok) throw new Error(data.error || t('error_verify'));
            onLogin(data.user, data.mustChangePassword);
        } catch(err) {
            setError(err.message);
        }
        finally { setLoading(false); }
    };

    const handleForgot = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await authFetch('/api/auth/forgot-password', {
                method: 'POST',
                body: JSON.stringify({ email: forgotEmail })
            });
            setForgotSent(true);
        } catch(err) {}
        setLoading(false);
    };

    const handleReset = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            setError(t('auth_passwords_mismatch'));
            return;
        }
        setLoading(true); setError('');
        try {
            const r = await authFetch('/api/auth/reset-password', {
                method: 'POST',
                body: JSON.stringify({ token: resetToken, password: newPassword })
            });
            const data = await r.json();
            if (!r.ok) throw new Error(data.error || t('error_reset'));
            setResetDone(true);
            window.history.replaceState({}, '', '/');
        } catch(err) { setError(err.message); }
        finally { setLoading(false); }
    };

    // ---- Reset password mode (via URL token) ----
    if (resetToken) {
        if (resetDone) return (
            <AuthCard title={t('auth_password_updated')}>
                <div className="pt-2 space-y-4 text-center">
                    <div className="p-3 bg-green-50 rounded-lg text-green-700 text-sm border border-green-100">{t('auth_password_updated_msg')}</div>
                    <button onClick={() => window.location.replace('/')} className="text-sm text-[#AA5D26] hover:underline">{t('auth_go_to_login')}</button>
                </div>
            </AuthCard>
        );
        return (
            <AuthCard title={t('auth_new_password_title')}>
                <form onSubmit={handleReset} className="pt-2 space-y-4">
                    {error && <div className="p-3 bg-red-50 rounded-lg text-sm text-red-600 border border-red-100">{error}</div>}
                    <div className="space-y-1">
                        <label className="block text-sm font-medium text-[#1A1A2E]">{t('auth_new_password')}</label>
                        <div className="relative">
                            <input type={showNewPw ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={8} autoFocus autoComplete="new-password"
                                className="w-full px-3 py-2 pr-10 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#AA5D26] focus:border-transparent" />
                            <button type="button" onClick={() => setShowNewPw(!showNewPw)} tabIndex={-1}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#AA5D26] transition-colors p-1">
                                <Icon name={showNewPw ? 'eye-off' : 'eye'} className="w-4 h-4" />
                            </button>
                        </div>
                        <p className="text-xs text-[#9CA3AF]">{t('auth_password_hint')}</p>
                    </div>
                    <div className="space-y-1">
                        <label className="block text-sm font-medium text-[#1A1A2E]">{t('auth_confirm_password')}</label>
                        <div className="relative">
                            <input type={showNewPw ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required minLength={8} autoComplete="new-password"
                                className={`w-full px-3 py-2 pr-10 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#AA5D26] focus:border-transparent ${confirmPassword && confirmPassword !== newPassword ? 'border-red-300 bg-red-50/50' : 'border-[#E5E7EB]'}`} />
                            <button type="button" onClick={() => setShowNewPw(!showNewPw)} tabIndex={-1}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#AA5D26] transition-colors p-1">
                                <Icon name={showNewPw ? 'eye-off' : 'eye'} className="w-4 h-4" />
                            </button>
                        </div>
                        {confirmPassword && confirmPassword !== newPassword && (
                            <p className="text-xs text-red-500">{t('auth_passwords_mismatch')}</p>
                        )}
                    </div>
                    <button type="submit" disabled={loading || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                        className="w-full py-2.5 bg-[#AA5D26] text-white rounded-lg text-sm font-semibold hover:bg-[#8B4513] transition-colors disabled:opacity-60">
                        {loading ? t('auth_updating') : t('auth_set_password')}
                    </button>
                </form>
            </AuthCard>
        );
    }

    // ---- Forgot password mode ----
    if (showForgot) {
        if (forgotSent) return (
            <AuthCard title={t('auth_email_sent_title')}>
                <div className="pt-2 space-y-4 text-center">
                    <div className="p-3 bg-green-50 rounded-lg text-green-700 text-sm border border-green-100">{t('auth_email_sent_msg')}</div>
                    <button onClick={() => { setShowForgot(false); setForgotSent(false); }} className="text-sm text-[#AA5D26] hover:underline">{t('auth_back_to_login')}</button>
                </div>
            </AuthCard>
        );
        return (
            <AuthCard title={t('auth_forgot_title')}>
                <form onSubmit={handleForgot} className="pt-2 space-y-4">
                    <div className="space-y-1">
                        <label className="block text-sm font-medium text-[#1A1A2E]">{t('auth_email')}</label>
                        <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} required autoFocus
                            className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#AA5D26] focus:border-transparent" />
                    </div>
                    <button type="submit" disabled={loading} className="w-full py-2.5 bg-[#AA5D26] text-white rounded-lg text-sm font-semibold hover:bg-[#8B4513] transition-colors disabled:opacity-60">
                        {loading ? t('auth_sending') : t('auth_send_reset')}
                    </button>
                    <div className="text-center">
                        <button type="button" onClick={() => setShowForgot(false)} className="text-sm text-[#9CA3AF] hover:text-[#AA5D26] transition-colors">{t('auth_back_to_login')}</button>
                    </div>
                </form>
            </AuthCard>
        );
    }

    // ---- 2FA verification screen ----
    if (show2FA) {
        return (
            <AuthCard title={t('twofa_title')}>
                <form onSubmit={handleVerify2FA} className="pt-2 space-y-4">
                    {error && <div className="p-3 bg-red-50 rounded-lg text-sm text-red-600 border border-red-100">{error}</div>}
                    <p className="text-sm text-[#4B5563] text-center">{t('twofa_code_label')}</p>
                    <input type="text" value={twofaCode} onChange={e => setTwofaCode(e.target.value)} required
                        inputMode="numeric" autoComplete="one-time-code" maxLength={8} autoFocus
                        placeholder="000000"
                        className="w-full px-3 py-3 border border-[#E5E7EB] rounded-lg text-center text-2xl font-mono tracking-[0.3em] focus:outline-none focus:ring-2 focus:ring-[#AA5D26] focus:border-transparent" />
                    <button type="submit" disabled={loading} className="w-full py-2.5 bg-[#AA5D26] text-white rounded-lg text-sm font-semibold hover:bg-[#8B4513] transition-colors disabled:opacity-60">
                        {loading ? t('twofa_verifying') : t('twofa_verify_btn')}
                    </button>
                    <p className="text-xs text-[#9CA3AF] text-center">{t('twofa_or_backup')}</p>
                    <div className="text-center">
                        <button type="button" onClick={() => { setShow2FA(false); setTempToken(null); setError(''); }}
                            className="text-sm text-[#9CA3AF] hover:text-[#AA5D26] transition-colors">{t('twofa_back')}</button>
                    </div>
                </form>
            </AuthCard>
        );
    }

    // ---- Default: login form ----
    return (
        <AuthCard title={t('auth_login_title')}>
            <form onSubmit={handleLogin} className="pt-2 space-y-4">
                {error && <div className="p-3 bg-red-50 rounded-lg text-sm text-red-600 border border-red-100">{error}</div>}
                <div className="space-y-1">
                    <label className="block text-sm font-medium text-[#1A1A2E]">{t('auth_email')}</label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" autoFocus
                        className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#AA5D26] focus:border-transparent" />
                </div>
                <div className="space-y-1">
                    <label className="block text-sm font-medium text-[#1A1A2E]">{t('auth_password')}</label>
                    <input type="password" value={password} onChange={e => setPassword(e.target.value)} required autoComplete="current-password"
                        className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#AA5D26] focus:border-transparent" />
                </div>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)}
                        className="w-4 h-4 rounded border-[#E5E7EB] text-[#AA5D26] focus:ring-[#AA5D26] accent-[#AA5D26]" />
                    <span className="text-sm text-[#4B5563]">{t('auth_remember_me')}</span>
                </label>
                <button type="submit" disabled={loading} className="w-full py-2.5 bg-[#AA5D26] text-white rounded-lg text-sm font-semibold hover:bg-[#8B4513] transition-colors disabled:opacity-60">
                    {loading ? t('auth_logging_in') : t('auth_login_btn')}
                </button>
                <div className="text-center">
                    <button type="button" onClick={() => setShowForgot(true)} className="text-sm text-[#9CA3AF] hover:text-[#AA5D26] transition-colors">{t('auth_forgot')}</button>
                </div>
            </form>
        </AuthCard>
    );
}
